import { SCORE_BAND_IDS } from "./stickyBannerScoreMatrix";

/**
 * Single source of truth for sticky banner copy (messageParts + CTA).
 * Page configs reference keys only — update copy here for campaigns / localization later.
 */

const DEFAULT_CTA = "Book a Session";
/** CTA for report score-matrix rows (product copy) */
const REPORT_MATRIX_CTA = "Talk to an Expert";

/** @type {Record<string, { messageParts: Array<{ kind: string; content: string }>; ctaText?: string }>} */
export const STICKY_MESSAGE_REGISTRY = {
  /* ----- Report route: score band × scroll milestone (REPORT_SCORE_SCROLL_MATRIX) ----- */
  "report.perfectScore.top": {
    ctaText: REPORT_MATRIX_CTA,
    messageParts: [{ kind: "text", content: "You're in the top 1% — what's next for you?" }]
  },
  "report.perfectScore.middle": {
    ctaText: REPORT_MATRIX_CTA,
    messageParts: [{ kind: "text", content: "You've aced every subject — find out how to stay there" }]
  },
  "report.perfectScore.bottom": {
    ctaText: REPORT_MATRIX_CTA,
    messageParts: [{ kind: "text", content: "Perfect score. Let's set a bigger target" }]
  },

  "report.highScore.top": {
    ctaText: REPORT_MATRIX_CTA,
    messageParts: [{ kind: "text", content: "You're close to the top — let's get you there" }]
  },
  "report.highScore.middle": {
    ctaText: REPORT_MATRIX_CTA,
    messageParts: [
      { kind: "text", content: "A few improvements in " },
      { kind: "highlight", content: "[Subject]" },
      { kind: "text", content: " could change everything" }
    ]
  },
  "report.highScore.middleFallback": {
    ctaText: REPORT_MATRIX_CTA,
    messageParts: [{ kind: "text", content: "A few improvements could change everything" }]
  },
  "report.highScore.bottom": {
    ctaText: REPORT_MATRIX_CTA,
    messageParts: [{ kind: "text", content: "Your next breakthrough score starts with the right strategy" }]
  },

  "report.mediumLowScore.top": {
    ctaText: REPORT_MATRIX_CTA,
    messageParts: [{ kind: "text", content: "Small improvements can lead to major score jumps" }]
  },
  "report.mediumLowScore.middle": {
    ctaText: REPORT_MATRIX_CTA,
    messageParts: [
      { kind: "text", content: "Need help with " },
      { kind: "highlight", content: "[Subject]" },
      { kind: "text", content: "? One expert session can fix that" }
    ]
  },
  "report.mediumLowScore.middleFallback": {
    ctaText: REPORT_MATRIX_CTA,
    messageParts: [{ kind: "text", content: "Need a little extra help? One expert session can fix that" }]
  },
  "report.mediumLowScore.bottom": {
    ctaText: REPORT_MATRIX_CTA,
    messageParts: [{ kind: "text", content: "The right guidance can completely change your next result" }]
  },

  "report.fallbackBand.top": {
    ctaText: REPORT_MATRIX_CTA,
    messageParts: [{ kind: "text", content: "One session can change your next score" }]
  },
  "report.fallbackBand.middle": {
    ctaText: REPORT_MATRIX_CTA,
    messageParts: [{ kind: "text", content: "An expert can fix your weak subjects in one session" }]
  },
  "report.fallbackBand.bottom": {
    ctaText: REPORT_MATRIX_CTA,
    messageParts: [{ kind: "text", content: "One session is all it takes to fix these gaps" }]
  },

  /* ----- Maths Olympiad: score band × milestone (no [Subject] placeholders) ----- */
  "mathsOlympiad.perfectScore.top": {
    ctaText: REPORT_MATRIX_CTA,
    messageParts: [{ kind: "text", content: "You're a Maths genius — what's next for you?" }]
  },
  "mathsOlympiad.perfectScore.middle": {
    ctaText: REPORT_MATRIX_CTA,
    messageParts: [{ kind: "text", content: "You've aced Maths — find out how to stay there" }]
  },
  "mathsOlympiad.perfectScore.bottom": {
    ctaText: REPORT_MATRIX_CTA,
    messageParts: [{ kind: "text", content: "Perfect in Maths. Let's set a bigger target" }]
  },
  "mathsOlympiad.highScore.top": {
    ctaText: REPORT_MATRIX_CTA,
    messageParts: [{ kind: "text", content: "You're so close to mastering Maths" }]
  },
  "mathsOlympiad.highScore.middle": {
    ctaText: REPORT_MATRIX_CTA,
    messageParts: [{ kind: "text", content: "A few improvements in Maths could change everything" }]
  },
  "mathsOlympiad.highScore.bottom": {
    ctaText: REPORT_MATRIX_CTA,
    messageParts: [{ kind: "text", content: "Your next Maths breakthrough starts with the right strategy" }]
  },
  "mathsOlympiad.mediumLowScore.top": {
    ctaText: REPORT_MATRIX_CTA,
    messageParts: [{ kind: "text", content: "Small improvements in Maths can lead to major score jumps" }]
  },
  "mathsOlympiad.mediumLowScore.middle": {
    ctaText: REPORT_MATRIX_CTA,
    messageParts: [{ kind: "text", content: "Let's strengthen your Maths performance" }]
  },
  "mathsOlympiad.mediumLowScore.bottom": {
    ctaText: REPORT_MATRIX_CTA,
    messageParts: [{ kind: "text", content: "The right guidance can completely change your Maths result" }]
  },
  "mathsOlympiad.fallbackBand.top": {
    ctaText: REPORT_MATRIX_CTA,
    messageParts: [{ kind: "text", content: "One session can change your Maths score" }]
  },
  "mathsOlympiad.fallbackBand.middle": {
    ctaText: REPORT_MATRIX_CTA,
    messageParts: [{ kind: "text", content: "An expert can fix your Maths gaps in one session" }]
  },
  "mathsOlympiad.fallbackBand.bottom": {
    ctaText: REPORT_MATRIX_CTA,
    messageParts: [{ kind: "text", content: "One session is all it takes to fix your Maths gaps" }]
  },

  /* ----- Report extension (Olympiad) — priority scroll ----- */
  "reportExtension.scroll.prelim": {
    ctaText: DEFAULT_CTA,
    messageParts: [
      { kind: "text", content: "An expert can " },
      { kind: "highlight", content: "fix your weak subjects" },
      { kind: "text", content: " in one session" }
    ]
  },
  "reportExtension.scroll.proficiency": {
    ctaText: DEFAULT_CTA,
    messageParts: [
      { kind: "text", content: "One session is all it takes to " },
      { kind: "highlight", content: "fix these gaps" }
    ]
  },
  "reportExtension.scroll.bottom": {
    ctaText: DEFAULT_CTA,
    messageParts: [
      { kind: "text", content: "One session is all it takes to " },
      { kind: "highlight", content: "fix these gaps" }
    ]
  },
  "reportExtension.default": {
    ctaText: DEFAULT_CTA,
    messageParts: [
      { kind: "text", content: "One session can change your next " },
      { kind: "highlight", content: "score" }
    ]
  },

  /* ----- EMSAT ----- */
  "emsat.scroll.highlights": {
    ctaText: DEFAULT_CTA,
    messageParts: [
      { kind: "text", content: "An expert can " },
      { kind: "highlight", content: "fix your weak subjects" },
      { kind: "text", content: " in one session" }
    ]
  },
  "emsat.scroll.bottom": {
    ctaText: DEFAULT_CTA,
    messageParts: [
      { kind: "text", content: "One session is all it takes to " },
      { kind: "highlight", content: "fix these gaps" }
    ]
  },
  "emsat.default": {
    ctaText: DEFAULT_CTA,
    messageParts: [
      { kind: "text", content: "One session can change your next " },
      { kind: "highlight", content: "score" }
    ]
  },

  /* ----- Exam page (static; no matrix / milestones) ----- */
  "exam.results.guidance": {
    ctaText: REPORT_MATRIX_CTA,
    messageParts: [{ kind: "text", content: "Get personalized guidance based on your results" }]
  },

  /* ----- Exam page carousel (legacy ids — configs may omit) ----- */
  "exam.carousel.1": {
    ctaText: DEFAULT_CTA,
    messageParts: [
      { kind: "text", content: "One session can change your next " },
      { kind: "highlight", content: "score" }
    ]
  },
  "exam.carousel.2": {
    ctaText: DEFAULT_CTA,
    messageParts: [
      { kind: "text", content: "An expert can " },
      { kind: "highlight", content: "fix your weak subjects" },
      { kind: "text", content: " in one session" }
    ]
  },
  "exam.carousel.3": {
    ctaText: DEFAULT_CTA,
    messageParts: [
      { kind: "text", content: "One session is all it takes to " },
      { kind: "highlight", content: "fix these gaps" }
    ]
  }
};

/**
 * When `[Subject]` cannot be resolved, swap to these registry ids (same milestone intent).
 */
export const REPORT_SUBJECT_FALLBACK_MESSAGE_IDS = {
  "report.highScore.middle": "report.highScore.middleFallback",
  "report.mediumLowScore.middle": "report.mediumLowScore.middleFallback"
};

/**
 * (scoreBand × scrollMilestone) → registry message id.
 * Milestones: top, middle, bottom (stable phase-driven).
 */
export const REPORT_SCORE_SCROLL_MATRIX = {
  [SCORE_BAND_IDS.PERFECT]: {
    top: "report.perfectScore.top",
    middle: "report.perfectScore.middle",
    bottom: "report.perfectScore.bottom"
  },
  [SCORE_BAND_IDS.HIGH]: {
    top: "report.highScore.top",
    middle: "report.highScore.middle",
    bottom: "report.highScore.bottom"
  },
  [SCORE_BAND_IDS.MEDIUM_LOW]: {
    top: "report.mediumLowScore.top",
    middle: "report.mediumLowScore.middle",
    bottom: "report.mediumLowScore.bottom"
  },
  [SCORE_BAND_IDS.FALLBACK]: {
    top: "report.fallbackBand.top",
    middle: "report.fallbackBand.middle",
    bottom: "report.fallbackBand.bottom"
  }
};

/** Same keys as {@link REPORT_SCORE_SCROLL_MATRIX}; Maths Olympiad copy */
export const MATHS_OLYMPIAD_SCORE_SCROLL_MATRIX = {
  [SCORE_BAND_IDS.PERFECT]: {
    top: "mathsOlympiad.perfectScore.top",
    middle: "mathsOlympiad.perfectScore.middle",
    bottom: "mathsOlympiad.perfectScore.bottom"
  },
  [SCORE_BAND_IDS.HIGH]: {
    top: "mathsOlympiad.highScore.top",
    middle: "mathsOlympiad.highScore.middle",
    bottom: "mathsOlympiad.highScore.bottom"
  },
  [SCORE_BAND_IDS.MEDIUM_LOW]: {
    top: "mathsOlympiad.mediumLowScore.top",
    middle: "mathsOlympiad.mediumLowScore.middle",
    bottom: "mathsOlympiad.mediumLowScore.bottom"
  },
  [SCORE_BAND_IDS.FALLBACK]: {
    top: "mathsOlympiad.fallbackBand.top",
    middle: "mathsOlympiad.fallbackBand.middle",
    bottom: "mathsOlympiad.fallbackBand.bottom"
  }
};
